import * as _ from "lodash";
import { ActionTypes } from './action-types';
import { ISwapState } from "./state";
import {
    IPairStatusChangeRequest, ICluster, IContact, ILogisticsProvider,
    ICenter, IListItem, IRccapRootCause, IUpdateClusterComment, IUpdateClusterSwapManager, IUpdateClusterDate,
    IUpdateClusterLogisticsProvider, IUpdateMatchHistoryDeadline,
    IUpdatePositionDateTime, IGpsAutomation, ITransportType, IPosition, ILogisticsOptionRequest, IMatchingSurgery, ICenterToCenterDistance
} from '@nkr/models/index';
import { PositionDateField, ClusterDateField, XmType, LogisticsOptionStatus } from "@nkr/enums/index";
import { ILogisticsUpdate } from './actions';

// reducer interfaces

interface ISetSwaps {
    type: string,
    list: ICluster[]
}

interface ISetStaffList {
    type: string,
    list: IContact[]
}

interface ISetLogisticsProviders {
    type: string,
    list: ILogisticsProvider[]
}

interface ISetCenterList {
    type: string,
    list: ICenter[]
}

interface ISetTransportTypeList {
    type: string,
    list: ITransportType[]
}

interface ISetListItemList {
    type: string,
    list: IListItem[]
}

interface ISetDistanceBetweenCentersList {
    type: string,
    list: ICenterToCenterDistance[]
}

interface ISetRccapCauseList {
    type: string,
    list: IRccapRootCause[]
}

interface IUpdatePairStatus {
    type: string,
    update: IPairStatusChangeRequest
}

interface IUpdateLogistics {
    type: string,
    update: ILogisticsUpdate
}

interface IUpdateSwapManager {
    type: string,
    update: IUpdateClusterSwapManager
}

interface IUpdateSwapLogisticsProvider {
    type: string,
    update: IUpdateClusterLogisticsProvider
}

interface IUpdateSwapDateTime {
    type: string,
    update: IUpdateClusterDate
}

interface IUpdateMatchOfferDeadlineDateTime {
    type: string,
    update: IUpdateMatchHistoryDeadline
}

interface IUpdateSwapComment {
    type: string,
    update: IUpdateClusterComment
}

interface IUpdateDateTimeForPosition {
    type: string,
    update: IUpdatePositionDateTime,
    isPickupDateOutsideRange: boolean,
    isDeliveryDateOutsideRange: boolean
}

interface IUpdateGps {
    type: string,
    update: {
        clusterId: number,
        positionNumber: number,
        toggle: boolean,
        gps: IGpsAutomation
    }
}

interface ISetSwapForCourier {
    type: string,
    position: IPosition
}

interface IUpdateSwapForLogisticsOption {
    type: string,
    update: ILogisticsOptionRequest
}

interface IUpdateMatchingSurgeriesAndRedeye {
    type: string,
    chainId: number,
    clusterNumber: number,
    positionNumber: number,
    isRecipient: boolean,
    matchingSurgeries: string[],
    recipientDeclinedDates: string[],
    shouldBeRedeye: boolean
}

interface IUpdateSwapForLogisticsOptionDeleteReturn {
    type: string,
    chainId: number,
    clusterNumber: number,
    positionNumber: number,
}

// reducer functions

const setSwaps = (state: ISwapState, action: ISetSwaps) => {
    state.list = action.list;
    return state;
}

const setClusterStatusList = (state: ISwapState, action: ISetListItemList) => {
    state.clusterStatusList = action.list;
    return state;
}

const setChainStatusList = (state: ISwapState, action: ISetListItemList) => {
    state.chainStatusList = action.list;
    return state;
}

const setStaffList = (state: ISwapState, action: ISetStaffList) => {
    state.staffList = action.list;
    return state;
}

const setLogisticsProviders = (state: ISwapState, action: ISetLogisticsProviders) => {
    state.logisticsProviders = action.list;
    return state;
}

const setPairStatusList = (state: ISwapState, action: ISetListItemList) => {
    state.pairStatusList = action.list;
    return state;
}

const setCenterList = (state: ISwapState, action: ISetCenterList) => {
    state.centerList = action.list;
    return state;
}

const setTransportTypeList = (state: ISwapState, action: ISetTransportTypeList) => {
    state.transportTypeList = action.list;
    return state;
}

const setSwapTypeList = (state: ISwapState, action: ISetListItemList) => {
    state.swapTypeList = action.list;
    return state;
}

const setDistanceBetweenCenters = (state: ISwapState, action: ISetDistanceBetweenCentersList) => {
    state.distanceBetweenCenters = action.list;
    return state;
}

const setRccapPhaseList = (state: ISwapState, action: ISetListItemList) => {
    state.rccapPhaseList = action.list;
    return state;
}

const setRccapAvoidableList = (state: ISwapState, action: ISetListItemList) => {
    state.rccapAvoidableList = action.list;
    return state;
}

const setRccapBillableList = (state: ISwapState, action: ISetListItemList) => {
    state.rccapBillableList = action.list;
    return state;
}

const setRccapCauseList = (state: ISwapState, action: ISetRccapCauseList) => {
    state.rccapCauseList = action.list;
    return state;
}

const setRccapCorrectiveActionList = (state: ISwapState, action: ISetListItemList) => {
    state.rccapCorrectiveActionList = action.list;
    return state;
}

const setRccapSatisfactoryResponseList = (state: ISwapState, action: ISetListItemList) => {
    state.rccapSatisfactoryResponseList = action.list;
    return state;
}

const updatePairStatus = (state: ISwapState, action: IUpdatePairStatus) => {
    var cluster = state.list.find((cluster) => {
        return cluster.chainId == action.update.chainId && cluster.clusterNumber == action.update.clusterNumber;
    });

    if (cluster != null) {
        var position = cluster.positions.find(p => p.positionNumber == action.update.positionNumber);

        if (position != null)
            position.pairStatusId = action.update.pairStatus;
    }

    return state;
}

const updateLogistics = (state: ISwapState, action: IUpdateLogistics) => {
    var cluster = state.list.find((cluster) => {
        return cluster.chainId == action.update.chainId && cluster.clusterNumber == action.update.clusterNumber;
    });

    if (cluster != null) {
        var position = cluster.positions.find(p => p.positionNumber == action.update.positionNumber);

        if (position != null) {
            position.kidneyPickup = action.update.kidneyPickup;
            position.kidneyArrival = action.update.kidneyArrival;

            if (position.donor.logistics != null) {
                position.donor.logistics.transportMethodActualId = action.update.transportMethodActual;
                position.donor.logistics.transportCostActual = action.update.transportCostActual != null ? action.update.transportCostActual.toString() : '';
            }
        }
    }

    return state;
}

const updateSwapManager = (state: ISwapState, action: IUpdateSwapManager) => {
    state.list = state.list.map(swap => {
        if (swap.id == action.update.clusterId)
            swap.swapManager = action.update.swapManager;

        if (action.update.updateAllClustersInChain && swap.chainId == action.update.chainId && swap.id != action.update.clusterId)
            swap.swapManager = action.update.swapManager;

        return swap;
    });

    return state;
}

const updateSwapLogisticsProvider = (state: ISwapState, action: IUpdateSwapLogisticsProvider) => {
    state.list = state.list.map(swap => {
        if (swap.id == action.update.clusterId)
            swap.courierVendor = action.update.logisticsProvider;

        return swap;
    });

    return state;
}

const updateSwapDateTime = (state: ISwapState, action: IUpdateSwapDateTime) => {
    state.list = state.list.map(swap => {
        if (swap.id == action.update.clusterId) {
            if (action.update.whichDate == ClusterDateField.LogisticsDateTime)
                swap.logisticsDateTime = action.update.dateTimeValue;

            if (action.update.whichDate == ClusterDateField.KickoffDateTime)
                swap.kickoffDateTime = action.update.dateTimeValue;
        }

        return swap;
    });

    return state;
}

const updateChainMatchOfferDeadlineDateTime = (state: ISwapState, action: IUpdateMatchOfferDeadlineDateTime) => {
    state.list = state.list.map(swap => {
        if (swap.id == action.update.clusterId) {
            swap.positions = swap.positions.map(position => {
                if (position.matchHistory)
                    position.matchHistory.deadlineDateTime = action.update.deadlineDateTime;

                return position;
            });
        }

        return swap;
    });

    return state;
}

const updateSwapComment = (state: ISwapState, action: IUpdateSwapComment) => {
    state.list = state.list.map(swap => {
        if (swap.id == action.update.clusterId)
            swap.note = action.update.comment;

        return swap;
    });

    return state;
}

const updatePositionDateTime = (state: ISwapState, action: IUpdateDateTimeForPosition) => {
    state.list = state.list.map(swap => {
        if (swap.chainId == action.update.chainId && swap.clusterNumber == action.update.clusterNumber) {
            swap.positions = swap.positions.map(position => {
                if (position.positionNumber == action.update.positionNumber) {
                    if (action.update.whichDate == PositionDateField.KidneyPickupDateTime) {
                        position.kidneyPickup = action.update.dateTimeValue;

                        if (position.positionNumber == 1) {
                            position.isPickupDateOutsideRange = action.isPickupDateOutsideRange;
                        }
                    }

                    if (action.update.whichDate == PositionDateField.KidneyArrivalDateTime) {
                        position.kidneyArrival = action.update.dateTimeValue;
                        position.isDeliveryDateOutsideRange = action.isDeliveryDateOutsideRange;
                    }

                    if (action.update.whichDate == PositionDateField.MatchOfferDeadlineDateTime)
                        position.matchHistory.deadlineDateTime = action.update.dateTimeValue;

                    if (action.update.whichDate == PositionDateField.CrossMatchDeadlineDateTime) {
                        var f = position.xmTracking.find(x => x.xmTypeId == XmType.FinalXm);

                        if (f)
                            f.xmStatusDeadline = action.update.dateTimeValue;
                    }
                }

                return position;
            });
        }

        return swap;
    });

    return state;
}

const updateGps = (state: ISwapState, action: IUpdateGps) => {
    state.list = state.list.map(swap => {
        if (swap.id == action.update.clusterId) {
            swap.positions = swap.positions.map(position => {
                if (position.positionNumber == action.update.positionNumber) {
                    position.gpsAutomation = action.update.gps;
                }

                return position;
            })
        }

        return swap;
    });

    return state;
}

const setSwapForCourier = (state: ISwapState, action: ISetSwapForCourier) => {
    state.list = state.list.map(swap => {
        if (swap.chainId == action.position.chainId && swap.clusterNumber == action.position.clusterNumber) {
            swap.positions = swap.positions.map(position => {
                if (position.positionNumber == action.position.positionNumber) {
                    return action.position;
                }

                return position;
            });
        }

        return swap;
    });

    return state;
}

const updateSwapForLogisticsOption = (state: ISwapState, action: IUpdateSwapForLogisticsOption) => {
    var draft = action.update.options.length > 0 ? action.update.options.filter(x => x.status == LogisticsOptionStatus.Draft).length == action.update.options.length : false;
    var submittedToNkr = action.update.options.length > 0 ? action.update.options.filter(x => x.status == LogisticsOptionStatus.SubmittedToNkr).length == action.update.options.length : false;
    var draftNkr = action.update.options.length > 0 ? action.update.options.filter(x => x.status == LogisticsOptionStatus.NkrDraft).length == action.update.options.length : false;
    var submittedToCenter = action.update.options.length > 0 ? action.update.options.filter(x => x.status == LogisticsOptionStatus.SubmittedToCenter).length == action.update.options.length : false;
    var accepted = action.update.options.length > 0 ? action.update.options.filter(x => x.status == LogisticsOptionStatus.SelectedByCenter).length == 1 : false;

    state.list = state.list.map(swap => {
        if (swap.chainId == action.update.chainId && swap.clusterNumber == action.update.clusterNumber) {
            swap.positions = swap.positions.map(position => {
                if (position.positionNumber == action.update.positionNumber) {
                    if (accepted) {
                        position.customLogisticsStatus = 5;
                    }
                    else {
                        if (submittedToCenter) {
                            position.customLogisticsStatus = 4;
                        }
                        else {
                            if (draftNkr) {
                                position.customLogisticsStatus = 3;
                            }
                            else {
                                if (submittedToNkr) {
                                    position.customLogisticsStatus = 2;
                                }
                                else {
                                    if (draft) {
                                        position.customLogisticsStatus = 0;
                                    }
                                }
                            }
                        }
                    }
                }

                return position;
            });
        }

        return swap;
    });

    return state;
}

const updateMatchingSurgeries = (state: ISwapState, action: IUpdateMatchingSurgeriesAndRedeye) => {
    state.list = state.list.map(swap => {
        if (swap.chainId == action.chainId && swap.clusterNumber == action.clusterNumber) {
            swap.positions = swap.positions.map(position => {
                if (position.positionNumber == action.positionNumber) {
                    if (!action.isRecipient) {
                        position.donorSurgeryConflicts = action.matchingSurgeries;
                    } else {
                        position.recipientSurgeryConflicts = action.matchingSurgeries;
                    }

                    position.recipientDeclinedDates = action.recipientDeclinedDates;

                    position.shouldBeRedeye = action.shouldBeRedeye;
                }

                return position;
            });
        }

        return swap;
    });

    return state;
}

const updateSwapForLogisticsOptionDeleteReturn = (state: ISwapState, action: IUpdateSwapForLogisticsOptionDeleteReturn) => {
    state.list = state.list.map(swap => {
        if (swap.chainId == action.chainId && swap.clusterNumber == action.clusterNumber) {
            swap.positions = swap.positions.map(position => {
                if (position.positionNumber == action.positionNumber) {
                    position.customLogisticsStatus = 0;
                }

                return position;
            });
        }

        return swap;
    });

    return state;
}

// reducer creation

var initialState: ISwapState = {
    clusterStatusId: 0,
    chainStatusId: 0,
    list: [],
    centerList: [],
    chainStatusList: [],
    clusterStatusList: [],
    logisticsProviders: [],
    pairStatusList: [],
    staffList: [],
    transportTypeList: [],
    swapTypeList: [],
    distanceBetweenCenters: [],
    rccapPhaseList: [],
    rccapAvoidableList: [],
    rccapBillableList: [],
    rccapCauseList: [],
    rccapCorrectiveActionList: [],
    rccapSatisfactoryResponseList: []
};

const createReducer = (initialState: ISwapState, handlers: any) => {
    return function reducer(state = initialState, action: any) {
        if (handlers.hasOwnProperty(action.type)) {
            let s = _.cloneDeep(state);
            return handlers[action.type](s, action);
        } else {
            return state;
        }
    }
}

const handlers = {
    [ActionTypes.SET_SWAPS]: setSwaps,
    [ActionTypes.SET_CLUSTERSTATUSLIST]: setClusterStatusList,
    [ActionTypes.SET_CHAINSTATUSLIST]: setChainStatusList,
    [ActionTypes.SET_STAFFLIST]: setStaffList,
    [ActionTypes.SET_LOGISTICSPROVIDERS]: setLogisticsProviders,
    [ActionTypes.SET_PAIRSTATUSLIST]: setPairStatusList,
    [ActionTypes.SET_CENTERLIST]: setCenterList,
    [ActionTypes.SET_TRANSPORTTYPELIST]: setTransportTypeList,
    [ActionTypes.SET_SWAPTYPELIST]: setSwapTypeList,
    [ActionTypes.SET_DISTANCE_BETWEEN_CENTERS]: setDistanceBetweenCenters,
    [ActionTypes.SET_RCCAPPHASELIST]: setRccapPhaseList,
    [ActionTypes.SET_RCCAPAVOIDABLELIST]: setRccapAvoidableList,
    [ActionTypes.SET_RCCAPBILLABLELIST]: setRccapBillableList,
    [ActionTypes.SET_RCCAPCAUSELIST]: setRccapCauseList,
    [ActionTypes.SET_RCCAPCORRECTIVEACTIONLIST]: setRccapCorrectiveActionList,
    [ActionTypes.SET_RCCAPSATISFACTORYRESPONSELIST]: setRccapSatisfactoryResponseList,
    [ActionTypes.SET_SWAP_FOR_COURIER]: setSwapForCourier,
    [ActionTypes.UPDATE_PAIR_STATUS]: updatePairStatus,
    [ActionTypes.UPDATE_LOGISTICS]: updateLogistics,
    [ActionTypes.UPDATE_SWAP_MANAGER]: updateSwapManager,
    [ActionTypes.UPDATE_LOGISTICS_PROVIDER]: updateSwapLogisticsProvider,
    [ActionTypes.UPDATE_KIDNEYPICKUP_DATETIME]: updatePositionDateTime,
    [ActionTypes.UPDATE_KIDNEYARRIVAL_DATETIME]: updatePositionDateTime,
    [ActionTypes.UPDATE_KICKOFF_DATETIME]: updateSwapDateTime,
    [ActionTypes.UPDATE_LOGISTICS_DATETIME]: updateSwapDateTime,
    [ActionTypes.UPDATE_MATCHOFFER_DEADLINE_DATETIME]: updatePositionDateTime,
    [ActionTypes.UPDATE_CROSSMATCH_DEADLINE_DATETIME]: updatePositionDateTime,
    [ActionTypes.UPDATE_COMMENT]: updateSwapComment,
    [ActionTypes.UPDATE_CHAIN_MATCHOFFER_DEADLINE_DATETIME]: updateChainMatchOfferDeadlineDateTime,
    [ActionTypes.UPDATE_GPS]: updateGps,
    [ActionTypes.UPDATE_SWAP_FOR_LOGISTICS_OPTION]: updateSwapForLogisticsOption,
    [ActionTypes.UPDATE_SWAP_FOR_LOGISTICS_OPTION_DELETE_RETURN]: updateSwapForLogisticsOptionDeleteReturn,
    [ActionTypes.UPDATE_MATCHING_SURGERIES]: updateMatchingSurgeries
}

export const Reducer = createReducer(initialState, handlers);
